import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { AuthService } from '@app/services/auth.service';
import { BadgeComponent, TooltipDirective } from 'ngx-lib';

interface User {
    firstName: string;
    lastName: string;
    roleName?: string;
    userFullName?: string;
    email?: string;
}

@Component({
    selector: 'app-user-info',
    templateUrl: './user-info.component.html',
    styleUrls: ['./user-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [BadgeComponent, TooltipDirective]
})
export class UserInfoComponent {
    users = input.required<User[]>();
    tooltipUseParentForPositioning = input<boolean>(false);

    heading = computed(() => {
        const users = this.updatedUsers();
        if (!users.length) return '';
        return this.getHeading(users);
    });

    subheading = computed(() => {
        const users = this.updatedUsers();
        if (!users.length) return '';
        return this.getSubheading(users);
    });

    tooltip = computed(() => {
        const users = this.updatedUsers();
        if (!users.length) return '';
        return this.getTooltip(users);
    });

    private readonly authService = inject(AuthService);

    // Computed signal that updates user data if account information changed
    private updatedUsers = computed(() => {
        const originalUsers = this.users();
        const currentUser = this.authService.user();

        if (!currentUser || !originalUsers.length) return originalUsers;

        // Map users and update the one that matches the current authenticated user
        return originalUsers.map((user) => {
            const isCurrentUser =
                user.email && currentUser.email && user.email.toLowerCase() === currentUser.email.toLowerCase();

            if (isCurrentUser) {
                // Return updated data
                return {
                    ...user,
                    firstName: currentUser.firstName,
                    lastName: currentUser.lastName,
                    userFullName: `${currentUser.firstName} ${currentUser.lastName}`
                } as User;
            }
            return user;
        });
    });

    private getHeading(users: User[]): string {
        const currentUser = users[0];
        if (!currentUser) return '';
        return currentUser.userFullName
            ? `${currentUser.userFullName?.trim()}`
            : `${currentUser.firstName.trim()} ${currentUser.lastName.trim()}`;
    }

    private getSubheading(users: User[]): string {
        if (users.length === 1) return users[0].roleName ?? '';
        return `+${users.length - 1}`;
    }

    private getTooltip(users: User[]): string {
        const [, ...rest] = users;
        if (!rest.length) return '';
        const restUsersData = rest.map((u) =>
            u.userFullName ? `${u.userFullName?.trim()}` : `${u.firstName.trim()} ${u.lastName.trim()}`
        );
        return restUsersData.join(',<br />');
    }
}
